import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsCustomHome,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  ExpandableNavigation,
  TextSize,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { TechDocsCustomStyles } from './components/techdocs/TechDocsCustomStyles';
import { TechDocsDownloadPdf } from './components/techdocs/TechDocsDownloadPdf';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';
import { aunoaLightTheme, aunoaDarkTheme } from './theme';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';

import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { PlaylistIndexPage, PlaylistPage } from '@backstage-community/plugin-playlist';
import { HomePage } from './components/home/HomePage';

import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { EntityValidationPage } from '@backstage-community/plugin-entity-validation';
import { CostInsightsPage } from '@backstage-community/plugin-cost-insights';

const app = createApp({
  apis,
  themes: [
    {
      id: 'aunoa-light',
      title: 'Aunoa Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={aunoaLightTheme} children={children} />
      ),
    },
    {
      id: 'aunoa-dark',
      title: 'Aunoa Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={aunoaDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <SignInPage
          {...props}
          auto
          providers={[
            {
              id: 'microsoft-auth-provider',
              title: 'Microsoft Entra ID',
              message: 'Sign in with your organization account',
              apiRef: microsoftAuthApiRef,
            },
            ...(process.env.NODE_ENV === 'development' ? ['guest' as const] : []),
          ]}
        />
      </div>
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/docs"
      element={
        <TechDocsCustomHome
          tabsConfig={[
            {
              label: 'All',
              panels: [
                {
                  title: 'All Documentation',
                  description: 'All entities with TechDocs configured',
                  panelType: 'DocsTable',
                  filterPredicate: entity =>
                    !!entity?.metadata?.annotations?.[
                      'backstage.io/techdocs-ref'
                    ],
                },
              ],
            },
            {
              label: 'Services',
              panels: [
                {
                  title: 'Service Documentation',
                  description: 'Documentation for services',
                  panelType: 'DocsCardGrid',
                  filterPredicate: entity =>
                    !!entity?.metadata?.annotations?.[
                      'backstage.io/techdocs-ref'
                    ] && entity?.spec?.type === 'service',
                },
              ],
            },
            {
              label: 'Libraries & SDKs',
              panels: [
                {
                  title: 'Libraries & SDK Documentation',
                  description: 'Documentation for libraries, SDKs, and reference docs',
                  panelType: 'DocsCardGrid',
                  filterPredicate: entity =>
                    !!entity?.metadata?.annotations?.[
                      'backstage.io/techdocs-ref'
                    ] &&
                    (entity?.spec?.type === 'library' ||
                      entity?.spec?.type === 'documentation'),
                },
              ],
            },
            {
              label: 'Websites',
              panels: [
                {
                  title: 'Website Documentation',
                  description: 'Documentation for websites and frontends',
                  panelType: 'DocsCardGrid',
                  filterPredicate: entity =>
                    !!entity?.metadata?.annotations?.[
                      'backstage.io/techdocs-ref'
                    ] && entity?.spec?.type === 'website',
                },
              ],
            },
          ]}
        />
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ExpandableNavigation />
        <ReportIssue />
        <TextSize />
        <Mermaid config={{ theme: 'default' }} />
        <TechDocsCustomStyles />
        <TechDocsDownloadPdf />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/playlist" element={<PlaylistIndexPage />} />
    <Route path="/playlist/:playlistId" element={<PlaylistPage />} />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route path="/entity-validation" element={<EntityValidationPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
